import React, { useMemo, useState, useRef } from 'react';
import DataGrid from '@dataGrid';
import type { MasterDetailContext } from './components/cellRenderer/ExpandCellRenderer';
import type { ColDef, GridApi } from 'ag-grid-community';
import { useDetailPanelLeftOffset } from './hooks/useDetailPanelLeftOffset';
import { useMasterDetailExpand } from './hooks/useMasterDetailExpand';
import { useMasterDetailGridBindings } from './hooks/useMasterDetailGridBindings';
import { useMasterDetailRowData } from './hooks/useMasterDetailRowData';
import { keepDetailRowsWithParent, normalizeMasterRowId } from './masterDetail/rowModel';
import type { MasterDetailGridProps } from './masterDetail/props';

export type { ColDef, GridApi, ICellRendererParams, IRowNode } from 'ag-grid-community';
export type { MasterDetailContext } from './components/cellRenderer/ExpandCellRenderer';
export type { MasterDetailGridProps } from './masterDetail/props';

export function MasterDetailGrid<TData extends object>({
  rowData: sourceData,
  columnDefs: userColumnDefs,
  getRowId,
  expandColumnId,
  detailCellRenderer,
  detailRowHeight = 220,
  getDetailRowHeight,
  shouldShowDetailRow,
  detailPanelAnchorField,
  detailPanelLeftOffsetFallback = 0,
  config,
  onGridReady,
  ...rest
}: MasterDetailGridProps<TData>) {
  const gridApiRef = useRef<GridApi | null>(null);
  const [masterGridApi, setMasterGridApi] = useState<GridApi | null>(null);

  const { isRowExpanded, toggleRow } = useMasterDetailExpand({
    gridApiRef,
    expandColumnId,
  });

  const detailPanelLeftOffset = useDetailPanelLeftOffset({
    masterGridApi,
    detailPanelAnchorField,
    detailPanelLeftOffsetFallback,
  });

  const rowData = useMasterDetailRowData({
    sourceData,
    getRowId,
    isRowExpanded,
    shouldShowDetailRow,
  });

  const context = useMemo<MasterDetailContext>(
    () => ({
      isExpanded: isRowExpanded,
      onExpandToggle: toggleRow,
      expanded: {
        has: (groupId: string) => isRowExpanded(groupId),
      },
      toggle: (groupId: string) => toggleRow(normalizeMasterRowId(groupId)),
      canShowDetailRow: (data: unknown) => shouldShowDetailRow?.(data as TData) !== false,
      detailPanelLeftOffset,
    }),
    [isRowExpanded, toggleRow, shouldShowDetailRow, detailPanelLeftOffset]
  );

  const {
    getRowIdFn,
    isFullWidthRow,
    getRowHeight,
    handleGridReady,
    handleRowDataUpdated,
  } = useMasterDetailGridBindings<TData>({
    gridApiRef,
    setMasterGridApi,
    detailRowHeight,
    getDetailRowHeight,
    onGridReady,
  });

  return (
    <DataGrid
      config={config}
      {...rest}
      rowData={rowData}
      columnDefs={userColumnDefs as ColDef[]}
      context={context}
      getRowId={getRowIdFn}
      isFullWidthRow={isFullWidthRow}
      getRowHeight={getRowHeight}
      fullWidthCellRenderer={detailCellRenderer}
      postSortRows={keepDetailRowsWithParent}
      onGridReady={handleGridReady}
      onRowDataUpdated={handleRowDataUpdated}
      embedFullWidthRows
    />
  );
}
